import { Card, Input, Radio, Select, Tag } from 'antd'
import { CommentIndicator } from 'features/libra/comments/CommentIndicator'
import { OverridableField } from 'features/libra/components/OverridableField'
import {
  calculateNiiStrategyType,
  deriveNiiJustification,
  getBaseCoverage,
  getCalculatedStrategyType,
  getEffectiveStrategyType,
  InspectionStrategy,
  niiPathReaches,
  NiiStrategy,
} from 'features/libra/stepComponents/zoneAnalysis/niiStrategy'
import { useZonePropertyAnchor } from 'features/libra/stepComponents/zoneAnalysis/useZonePropertyAnchor'
import { ZoneTabPrefillMeta } from 'features/libra/stepComponents/zoneAnalysis/zoneAnalysisApi'
import { STRATEGY_FIELD } from 'features/libra/stepComponents/zoneAnalysis/zoneOverrides'
import { CheckCircle, HelpCircle } from 'lucide-react'
import { FC } from 'react'
import styled, { useTheme } from 'styled-components'

import { AiPrefillFieldState } from './AiPrefillFieldState'

const { TextArea } = Input

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`

const HelpText = styled.p`
  font-size: 13px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  margin: 0 0 12px 0;
`

const FieldGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const Label = styled.label`
  font-size: 12px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.secondaryText};
  text-transform: uppercase;
  letter-spacing: 0.5px;
`

const DecisionTreeCard = styled(Card)`
  margin-bottom: 16px;

  .ant-card-body {
    padding: 16px;
  }
`

const QuestionRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  margin-bottom: 16px;
  padding: 12px;
  background: ${({ theme }) => theme.forecast.background2};
  border-radius: 6px;
`

const QuestionIcon = styled.div<{ $answered?: boolean }>`
  color: ${({ $answered, theme }) =>
    $answered ? '#4caf50' : theme.forecast.secondaryText};
  flex-shrink: 0;
  margin-top: 2px;
`

const QuestionContent = styled.div`
  flex: 1;
`

const QuestionText = styled.div`
  font-weight: 500;
  margin-bottom: 8px;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const QuestionRequirement = styled.span<{
  $state: 'required' | 'conditional' | 'not-required'
}>`
  display: inline-block;
  margin-left: 8px;
  font-size: 11px;
  font-weight: 500;
  color: ${({ $state, theme }) =>
    $state === 'required'
      ? (theme.forecast.warning ?? '#faad14')
      : theme.forecast.tertiaryText};
`

const PendingStrategy = styled.div`
  padding: 12px 14px;
  border: 1px dashed ${({ theme }) => theme.forecast.border};
  border-radius: 8px;
  color: ${({ theme }) => theme.forecast.secondaryText};
  font-size: 12px;
`

const StrategyResult = styled.div<{ $type: InspectionStrategy }>`
  padding: 20px;
  border-radius: 12px;
  background: ${({ theme }) => theme.forecast.background2};
  border-left: 4px solid
    ${({ $type }) =>
      $type === 'A' ? '#4caf50' : $type === 'B' ? '#ff9800' : '#f44336'};
  margin-top: 16px;
  border: 1px solid ${({ theme }) => theme.forecast.border};
  border-left-width: 4px;
`

const StrategyTitle = styled.div<{ $type: InspectionStrategy }>`
  font-size: 18px;
  font-weight: 700;
  margin-bottom: 12px;
  color: ${({ $type }) =>
    $type === 'A' ? '#2e7d32' : $type === 'B' ? '#e65100' : '#c62828'};
  display: flex;
  align-items: center;
  gap: 8px;
`

const StrategyDescription = styled.div`
  font-size: 14px;
  line-height: 1.6;
  color: ${({ theme }) => theme.forecast.defaultText};
  margin-bottom: 16px;
`

const CoverageHighlight = styled.div<{ $type: InspectionStrategy }>`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 12px 16px;
  border-radius: 6px;
  background: ${({ theme }) => theme.forecast.background3};
  border: 1px solid ${({ theme }) => theme.forecast.border};
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.forecast.defaultText};
`

const CoverageValue = styled.span<{ $type: InspectionStrategy }>`
  font-size: 20px;
  font-weight: 700;
  color: ${({ $type }) =>
    $type === 'A' ? '#2e7d32' : $type === 'B' ? '#e65100' : '#c62828'};
`

export interface NiiStrategyPanelProps {
  strategy?: NiiStrategy
  onChange: (strategy: NiiStrategy) => void
  readonly?: boolean
  zoneId: string
  aiPrefill?: ZoneTabPrefillMeta
}

export const NiiStrategyPanel: FC<NiiStrategyPanelProps> = ({
  strategy,
  onChange,
  readonly,
  zoneId,
  aiPrefill,
}) => {
  const anchor = useZonePropertyAnchor(zoneId)
  const theme = useTheme()

  const degradationLikely = strategy?.degradation_likely
  const degradationPredictable = strategy?.degradation_predictable
  const degradationSeverity = strategy?.degradation_severity

  const effectiveStrategy = getEffectiveStrategyType(strategy)

  const baseCoverage = effectiveStrategy
    ? getBaseCoverage(effectiveStrategy)
    : undefined

  // Justifications render as authored — no scrubbing here. The backend no
  // longer emits null-sentinel prose (gong-agent #54) and the AI ingestion path
  // (`zoneTabAutofill`) is the belt-and-braces net; scrubbing an SME's live
  // keystrokes in the panel is a downstream band-aid (CLAUDE.md #3).
  const degradationLikelyJustification =
    strategy?.degradation_likely_justification
  const degradationPredictableJustification =
    strategy?.degradation_predictable_justification
  const degradationSeverityJustification =
    strategy?.degradation_severity_justification
  const { predictable: reachesQ2, severity: reachesQ3 } =
    niiPathReaches(strategy)

  const q2Requirement =
    degradationLikely === true
      ? 'required'
      : degradationLikely === false
        ? 'not-required'
        : 'conditional'
  const q3Requirement =
    degradationLikely === true && degradationPredictable === true
      ? 'required'
      : degradationLikely === false || degradationPredictable === false
        ? 'not-required'
        : 'conditional'

  const handleUpdate = (updates: Partial<NiiStrategy>) => {
    const merged = { ...strategy, ...updates }
    const likelyValue = merged.degradation_likely
    const updatedPredictable = merged.degradation_predictable
    const updatedSeverity = merged.degradation_severity
    const likelyJustification = merged.degradation_likely_justification
    const updatedPredictableJust = merged.degradation_predictable_justification
    const updatedSeverityJust = merged.degradation_severity_justification
    const next: NiiStrategy = {
      strategy_type_calculated: calculateNiiStrategyType(merged),
      degradation_likely: likelyValue,
      degradation_likely_justification: likelyJustification,
      degradation_predictable: updatedPredictable,
      degradation_predictable_justification: updatedPredictableJust,
      degradation_severity: updatedSeverity,
      degradation_severity_justification: updatedSeverityJust,
      strategy_type_override: merged.strategy_type_override,
      strategy_type_override_justification:
        merged.strategy_type_override_justification,
    }
    onChange({ ...next, justification: deriveNiiJustification(next) })
  }

  const strategyInfo = {
    A: {
      title: 'Type A - Confirmation Strategy',
      description:
        'Degradation is not expected to be measurable within two inspection intervals. Inspection aims to confirm assumptions.',
      baseCoverage: '7.5%',
    },
    B: {
      title: 'Type B - Quantitative Strategy (Predictable)',
      description:
        'Degradation is predictable and measurable. Inspection quantifies degradation rates and patterns.',
      baseCoverage: '25%',
    },
    C: {
      title: 'Type C - Quantitative Strategy (Unpredictable)',
      description:
        'Degradation is unpredictable or aggressive. More comprehensive inspection required to capture worst-case scenarios.',
      baseCoverage: '100%',
    },
  }

  return (
    <Panel>
      <HelpText>
        Determine the appropriate NII strategy for this zone by answering the
        decision tree questions below. The strategy determines the base
        inspection coverage requirement.
      </HelpText>

      <DecisionTreeCard title='Decision Tree'>
        <CommentIndicator anchor={anchor('nii_strategy.degradation_likely')}>
          <QuestionRow>
            <QuestionIcon $answered={degradationLikely !== undefined}>
              {degradationLikely !== undefined ? (
                <CheckCircle size={20} />
              ) : (
                <HelpCircle size={20} />
              )}
            </QuestionIcon>
            <QuestionContent>
              <QuestionText>
                Q1: Is degradation likely or measurable within two inspection
                intervals?
                <QuestionRequirement $state='required'>
                  Required
                </QuestionRequirement>
                <AiPrefillFieldState
                  meta={aiPrefill}
                  field='degradation_likely'
                  empty={degradationLikely === undefined}
                />
              </QuestionText>
              <Radio.Group
                value={degradationLikely}
                onChange={(e) =>
                  handleUpdate({ degradation_likely: e.target.value })
                }
                disabled={readonly}>
                <Radio value={true}>Yes - Degradation expected</Radio>
                <Radio value={false}>No - Degradation unlikely</Radio>
              </Radio.Group>
              <TextArea
                style={{ marginTop: 8 }}
                value={degradationLikelyJustification ?? ''}
                onChange={(e) =>
                  handleUpdate({
                    degradation_likely_justification:
                      e.target.value || undefined,
                  })
                }
                placeholder='Justify your answer to Q1 — e.g., based on process conditions, historical inspection data, or known corrosion mechanisms...'
                rows={2}
                disabled={readonly}
              />
            </QuestionContent>
          </QuestionRow>
        </CommentIndicator>

        <CommentIndicator
          anchor={anchor('nii_strategy.degradation_predictable')}>
          <QuestionRow>
            <QuestionIcon $answered={degradationPredictable !== undefined}>
              {degradationPredictable !== undefined ? (
                <CheckCircle size={20} />
              ) : (
                <HelpCircle size={20} />
              )}
            </QuestionIcon>
            <QuestionContent>
              <QuestionText>
                Q2: Is degradation predictable?
                <QuestionRequirement $state={q2Requirement}>
                  {q2Requirement === 'required'
                    ? 'Required'
                    : q2Requirement === 'not-required'
                      ? 'Not required for the current path'
                      : 'Required if Q1 is Yes'}
                </QuestionRequirement>
                <AiPrefillFieldState
                  meta={aiPrefill}
                  field='degradation_predictable'
                  empty={
                    q2Requirement === 'required' &&
                    degradationPredictable === undefined
                  }
                />
              </QuestionText>
              <Radio.Group
                value={degradationPredictable}
                onChange={(e) =>
                  handleUpdate({ degradation_predictable: e.target.value })
                }
                disabled={readonly || !reachesQ2}>
                <Radio value={true}>Yes - Predictable pattern</Radio>
                <Radio value={false}>No - Unpredictable</Radio>
              </Radio.Group>
              <TextArea
                style={{ marginTop: 8 }}
                value={degradationPredictableJustification ?? ''}
                onChange={(e) =>
                  handleUpdate({
                    degradation_predictable_justification:
                      e.target.value || undefined,
                  })
                }
                placeholder='Justify your answer to Q2 — e.g., whether a consistent degradation rate exists based on process chemistry, temperature, or prior measurements...'
                rows={2}
                disabled={readonly || !reachesQ2}
              />
            </QuestionContent>
          </QuestionRow>
        </CommentIndicator>

        <CommentIndicator anchor={anchor('nii_strategy.degradation_severity')}>
          <QuestionRow>
            <QuestionIcon $answered={degradationSeverity !== undefined}>
              {degradationSeverity !== undefined ? (
                <CheckCircle size={20} />
              ) : (
                <HelpCircle size={20} />
              )}
            </QuestionIcon>
            <QuestionContent>
              <QuestionText>
                Q3: What is the degradation severity?
                <QuestionRequirement $state={q3Requirement}>
                  {q3Requirement === 'required'
                    ? 'Required'
                    : q3Requirement === 'not-required'
                      ? 'Not required for the current path'
                      : 'Required if Q1 and Q2 are Yes'}
                </QuestionRequirement>
                <AiPrefillFieldState
                  meta={aiPrefill}
                  field='degradation_severity'
                  empty={
                    q3Requirement === 'required' &&
                    degradationSeverity === undefined
                  }
                />
              </QuestionText>
              <Select
                value={degradationSeverity}
                onChange={(value) =>
                  handleUpdate({ degradation_severity: value })
                }
                placeholder='Select severity'
                style={{ width: '100%' }}
                disabled={readonly || !reachesQ3}>
                <Select.Option value='low'>Low</Select.Option>
                <Select.Option value='medium'>Medium</Select.Option>
                <Select.Option value='high'>High</Select.Option>
              </Select>
              <TextArea
                style={{ marginTop: 8 }}
                value={degradationSeverityJustification ?? ''}
                onChange={(e) =>
                  handleUpdate({
                    degradation_severity_justification:
                      e.target.value || undefined,
                  })
                }
                placeholder='Justify the severity rating — e.g., expected corrosion rates, wall thickness loss, known worst-case scenarios...'
                rows={2}
                disabled={readonly || !reachesQ3}
              />
            </QuestionContent>
          </QuestionRow>
        </CommentIndicator>
      </DecisionTreeCard>

      {effectiveStrategy ? (
        <StrategyResult $type={effectiveStrategy}>
          <StrategyTitle $type={effectiveStrategy}>
            {effectiveStrategy === InspectionStrategy.A
              ? '✓'
              : effectiveStrategy === InspectionStrategy.B
                ? '◆'
                : '⚠'}{' '}
            {strategyInfo[effectiveStrategy].title}
            {strategy?.strategy_type_override && (
              <Tag color='orange' style={{ marginLeft: 8 }}>
                Overridden
              </Tag>
            )}
          </StrategyTitle>
          <StrategyDescription>
            {strategyInfo[effectiveStrategy].description}
          </StrategyDescription>
          <CoverageHighlight $type={effectiveStrategy}>
            <span>Base Coverage:</span>
            <CoverageValue $type={effectiveStrategy}>
              {baseCoverage}%
            </CoverageValue>
          </CoverageHighlight>
          <div
            style={{
              fontSize: 12,
              color: theme.forecast.tertiaryText,
              marginTop: 8,
              fontStyle: 'italic',
            }}>
            This base coverage will be adjusted by risk factors to determine
            final required coverage.
          </div>
        </StrategyResult>
      ) : (
        <PendingStrategy>
          Complete the yellow required question to calculate the NII strategy.
          AI answers for later questions remain visible and will be used if
          their branch becomes applicable.
        </PendingStrategy>
      )}

      <CommentIndicator anchor={anchor('nii_strategy.strategy_type_override')}>
        <FieldGroup>
          <Label>Manual Override (if needed)</Label>
          <OverridableField
            label={STRATEGY_FIELD.label}
            calculatedValue={getCalculatedStrategyType(strategy)}
            overrideValue={strategy?.strategy_type_override}
            justification={strategy?.strategy_type_override_justification}
            formatValue={(value) => strategyInfo[value].title}
            renderEditor={(value, setValue) => (
              <Select<InspectionStrategy>
                value={value}
                onChange={setValue}
                placeholder='Select a strategy'>
                <Select.Option value={InspectionStrategy.A}>
                  Type A - Confirmation
                </Select.Option>
                <Select.Option value={InspectionStrategy.B}>
                  Type B - Quantitative (Predictable)
                </Select.Option>
                <Select.Option value={InspectionStrategy.C}>
                  Type C - Quantitative (Unpredictable)
                </Select.Option>
              </Select>
            )}
            onChange={(value, justification) =>
              handleUpdate({
                strategy_type_override: value,
                strategy_type_override_justification: justification,
              })
            }
            readonly={readonly}
          />
        </FieldGroup>
      </CommentIndicator>
    </Panel>
  )
}
